'use client';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@features/ui/components/dropdown-menu';

import {memo} from 'react';
import {cn} from '@features/ui/utils';
import {useExportButtonDisabled, useExportEnabled} from './useExportToFigma';
import DropdownArrow from '@/icons/dropdown-arrow-new.svg';
import WebIcon from '@/icons/web.svg';
import MobileIcon from '@/icons/mobile.svg';
import ChangeVariantIcon from '@/icons/change-variant.svg';
import CheckIcon from '@/icons/check-icon.svg';
import {useConvertScreenType} from '../hooks/useConvertScreenType';
import {useHasActiveStructure} from './canvas-feedback-buttons';
import type {GenType} from '@features/flow/flowService';

type CanvasScreenTypeDropdownProps = {
  screenId: string;
  genType: GenType;
  flowId: string;
};

export const CanvasScreenTypeDropdown = memo(
  ({screenId, genType, flowId}: CanvasScreenTypeDropdownProps) => {
    const exportEnabled = useExportEnabled();
    const isDisabled = useExportButtonDisabled();
    const hasActiveStructure = useHasActiveStructure(screenId);
    const {convertScreenType, isPending} = useConvertScreenType();

    if (!exportEnabled) return null;

    const buttonDisabled = isDisabled || isPending || !hasActiveStructure;

    const isMobile = genType === 'mobile-html-css';
    const CurrentIcon = isMobile ? MobileIcon : WebIcon;
    const OppositeIcon = isMobile ? WebIcon : MobileIcon;
    const currentLabel = isMobile ? 'Mobile' : 'Desktop';
    const oppositeLabel = isMobile ? 'Desktop' : 'Mobile';

    const handleConvert = () => {
      void convertScreenType(screenId, genType, flowId);
    };

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className={cn(
              'flex h-full items-center rounded-md px-1 text-sm font-semibold text-white transition-colors',
              !buttonDisabled && 'hover:bg-white/20',
              buttonDisabled && 'opacity-50',
            )}
            disabled={buttonDisabled}
          >
            <CurrentIcon className='size-6' />
            <DropdownArrow className='-ml-1 size-4 text-white/60' />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align='center'
          sideOffset={8}
          className='w-52 [&>*]:gap-0.5 [&>*]:px-1.5 [&_svg]:size-6'
        >
          <DropdownMenuItem disabled className='opacity-50'>
            <CurrentIcon />
            {currentLabel}
            <CheckIcon className='ml-auto !h-5 !w-5' />
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleConvert}>
            <OppositeIcon />
            <span>Generate {oppositeLabel}</span>
            <ChangeVariantIcon className='ml-auto !size-6' />
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  },
);
CanvasScreenTypeDropdown.displayName = 'CanvasScreenTypeDropdown';
